import { updateServiceLocation } from '@/actions/updateServiceLocation';
import { DEFAULT_DEVICE_FAMILY_NAME } from '@/constants/amc';
import { D365_USER_ID } from '@/constants/profileManagement';
import { useAppConfigContext } from '@/context/AppContext';
import { useProductContext } from '@/context/productContext';
import { useServiceContext } from '@/context/serviceRequestContext';
import useToast from '@/hooks/useToast';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import Button from '@/shared/Button';
import Div from '@/shared/Div';
import Image from '@/shared/Image';
import Paragraph from '@/shared/Paragraph';
import { getAddress } from '@/utils/address';
import { isApplicableForAMCUpSell } from '@/utils/amcPlans';
import Cookies from '@/utils/cookie';
import SessionStorage from '@/utils/sessionStorage';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import Locator from '../Icons/Locator';
import { getEnvValue } from '@/utils/envUtils';
import WarningExclaimation from '../Icons/WarningExclaimation';

const API_KEY = getEnvValue('GOOGLE_MAP_API_KEY');

type AddressConfirmationProps = {
  closeModal: () => void;
  address?: any;
  latLong?: any;
  assetId?: string;
  preciseLocation?: string;
};
function AddressConfirmation({
  address,
  closeModal,
  latLong,
  preciseLocation,
}: AddressConfirmationProps) {
  const fetch = (...args: any) => updateServiceLocation.apply(null, args);
  const serviceLocation = useTriggerRequest({ fetch });
  const { triggerDefault } = useToast();
  const handleBackClick = () => {
    closeModal();
  };
  const router = useRouter();
  const {
    state: { config },
  } = useAppConfigContext();

  const {
    state: { selectedProduct },
  } = useProductContext();
  const { id, deviceId, deviceFamilyName, expiryStatus } = selectedProduct;
  const { dispatch: serviceAction, state: serviceState } = useServiceContext();
  const { serviceType, isRescheduleFlow } = serviceState;
  const { flatNo, building, area, pincode, landmark, subArea, state, city } = address;
  const displayedAddress = {
    house_number: flatNo,
    address1: building,
    address2: pincode,
    address3: subArea,
    address4: area,
    state,
    city,
    postal_code: pincode,
  };

  const handleConfirmClick = async () => {
    const reqBody = {
      houseNo: flatNo || '',
      addressLine1: building || '',
      addressLine2: landmark || '',
      addressLine3: subArea || '',
      addressLine4: area || '',
      latitude: latLong?.lat,
      longitude: latLong?.lng,
      pincode,
      d365UserId: Cookies.get(D365_USER_ID) || '',
      assetId: id,
    };

    try {
      await serviceLocation.promisifyTrigger(reqBody);
      const address = {
        house_number: flatNo,
        address1: building,
        address2: pincode,
        address3: subArea,
        address4: area,
        state,
        city,
        postal_code: pincode,
      };
      SessionStorage.set('address', getAddress(address));
    } catch (error) {
      console.log('Error updating address of the customer', error);
    }

    if (isApplicableForAMCUpSell(config, serviceType, selectedProduct, isRescheduleFlow)) {
      router.push({
        pathname: `/amc-service-plans/${id}/${deviceId}`,
        query: {
          assets: true,
          deviceFamilyName: deviceFamilyName ?? DEFAULT_DEVICE_FAMILY_NAME,
          map: true,
        },
      });
    } else if (
      (expiryStatus?.status === 'IW' || expiryStatus?.status === 'IC') &&
      serviceState.isPrepaidAllowed
    ) {
      router.push({
        pathname: `/service/${id}/prepaid`,
        query: { schedulePrepaid: true },
      });
    } else {
      // router.replace({
      //   pathname: router.pathname,
      //   query: { ...router.query, map: true },
      // });
      serviceAction.changeComponent('serviceReqSummary');
    }
  };
  useEffect(() => {
    if (serviceLocation?.isSuccess) {
      triggerDefault({
        message: serviceLocation?.data?.data,
        durationInms: 8000,
      });
    }
    if (serviceLocation?.isError) {
      triggerDefault({
        message: serviceLocation?.error?.error,
        durationInms: 5000,
      });
    }
  }, [serviceLocation?.isError, serviceLocation?.isSuccess]);

  return (
    <Div className="flex flex-col p-s5 md:p-s6 gap-s5 md:w-[400px]">
      <Paragraph className="text-body-xl font-bold">Confirm Sevice Location</Paragraph>

      <Div className="flex flex-col gap-s3">
        <Div className="flex gap-s2">
          <Locator width={24} />
          <Paragraph className="text-h-4">{preciseLocation}</Paragraph>
        </Div>
        <Paragraph>{getAddress(displayedAddress)}</Paragraph>
      </Div>
      <Div className="rounded-lg" style={{ position: 'relative', display: 'inline-block' }}>
        <Image
          src={`https://maps.googleapis.com/maps/api/staticmap?center=${latLong.lat},${latLong.lng}&zoom=18&size=600x300&markers=color:red%7C${latLong.lat},${latLong.lng}&key=${API_KEY}`}
          alt="Selected address map"
          width={600}
          height={300}
          style={{ borderRadius: '8px' }}
        />
        <Div className="absolute  top-1/2 left-1/2  -translate-x-1/2 -translate-y-[215%] px-s3 py-s2  rounded text-body-sm bg-white ">
          <b>Your location</b>
        </Div>
      </Div>
      <Div className="p-s4 gap-s2 border rounded-lg border-orange-200 bg-orange-100">
        <Div className="flex gap-s2">
          <WarningExclaimation />
          <Paragraph className="text-orange-500 font-bold">
            Important! Check if Location is Correct
          </Paragraph>
        </Div>
        <Paragraph> A wrong location could delay your service.</Paragraph>
      </Div>

      <Div className="flex gap-s3 py-s5">
        <Button className="w-full" onClick={handleBackClick} theme="secondary">
          Go Back
        </Button>
        <Button
          className="w-full"
          onClick={handleConfirmClick}
          isLoading={serviceLocation.isLoading}
        >
          Confirm
        </Button>
      </Div>
    </Div>
  );
}

export default AddressConfirmation;
